import { Component, ComponentFactoryResolver, ComponentRef, EventEmitter, Input, OnChanges, OnDestroy, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { HhsgSharedAdminTab } from '../../../models/admin-tab.model';
import { HhsgSharedFormStateService, HhsgSharedTabControlService } from '../../../services';
import { HhsgSharedAdminBaseComponent } from '../base.component';
import { HhsgSharedTabDirective } from './tab.directive';
import { HhsgSharedTabInterface } from './tab.interface';

@Component({
    selector: 'hhsg-dynamic-tab',
    templateUrl: './dynamic-tab.component.html',
    styleUrls: ['./dynamic-tab.component.scss']
})
export class HhsgSharedDynamicTabComponent extends HhsgSharedAdminBaseComponent implements OnChanges, OnDestroy {
    @Input() currentTab: HhsgSharedAdminTab;
    @Input() isSaveTab: boolean;
    @Output() errorMessages = new EventEmitter<string[]>();
    @Output() saveButtonClicked = new EventEmitter();
    @Output() cancelButtonClicked = new EventEmitter();

    @ViewChild(HhsgSharedTabDirective, { static: true }) tabHost: HhsgSharedTabDirective;

    public confirmCancel = false;
    public confirmSaving = false;

    private componentRef: ComponentRef<any>;
    private instance: HhsgSharedTabInterface;

    private _reloadTabSubscription: Subscription;
    private _unsubscribe$ = new Subject<void>();

    constructor(private componentFactoryResolver: ComponentFactoryResolver,
        protected _formStateService: HhsgSharedFormStateService,
        protected _routerService: Router,
        private _tabControlService: HhsgSharedTabControlService) {
        super(_formStateService, _routerService);

        this._reloadTabSubscription = this._tabControlService.reloadTab$
                                                             .pipe(takeUntil(this._unsubscribe$))
                                                             .subscribe(tabId => {
                                                                if (this.currentTab.id == tabId) {
                                                                    this.instance.reload();
                                                                }
                                                             });
    }

    public async ngOnChanges() {
        await this.loadComponent();
    }

    public ngOnDestroy(): void {
        if (this._reloadTabSubscription) {
            this._reloadTabSubscription.unsubscribe();
        }

        this._unsubscribe$.next();
        this._unsubscribe$.complete();
    }

    private async cancel(): Promise<void> {
        this._tabControlService.announceTabLoading(this.currentTab.id);

        await this.instance.cancel();

        this._formStateService.emitClearAKAEvent();

        this._tabControlService.announceTabFinishedLoading(this.currentTab.id);
    }

    public get isDisabled(): boolean {
        let isDisabled = (this.instance?.isDisabled()) ?? false;


        this.currentTab.isDisabled = isDisabled;
        this._tabControlService.announceTab(this.currentTab);

        return isDisabled;
    }

    public get isFormDirty(): boolean {
        let isDirty = (this.instance?.isFormDirty()) ?? false;


        this.currentTab.isDirty = isDirty;
        this._tabControlService.announceTab(this.currentTab);

        return isDirty;
    }

    public get isFormValid(): boolean {
        return this.instance?.isFormValid();
    }

    private async loadComponent(): Promise<void> {
        if (!this.currentTab.component) {
            return;
        }
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.currentTab.component);

        const viewContainerRef = this.tabHost.viewContainerRef;
        viewContainerRef.clear();

        this.componentRef = viewContainerRef.createComponent(componentFactory);

        this.instance = (<HhsgSharedTabInterface>this.componentRef.instance);

        this.instance.tab = this.currentTab;
    }

    public async onCancelEditing(): Promise<void> {
        this.confirmCancel = true;
    }

    public onConfirmCancel(): void {
        this._formStateService.continueArgs = arguments;
        this._formStateService.continueFunc = async () => await this.cancel();

        super.onDeactivateLeavePage();

        this.confirmCancel = false;

        this.errorMessages.emit([]);
        this.cancelButtonClicked.emit();
    }

    public async onConfirmSaving(): Promise<void> {
        this.confirmSaving = false;

        let saveResult = await this.save();

        this.errorMessages.emit(this.instance.errorMessages);

        if (saveResult && saveResult > 0) {
            this.saveButtonClicked.emit();
        }
    }

    public async onSubmit(): Promise<void> {
        this.confirmSaving = this.instance.isFormValid();

        if (this.confirmSaving) {
            this.errorMessages.emit([]);
        }
    }

    private async save(): Promise<number> {
        this._tabControlService.announceTabLoading(this.currentTab.id);

        let result = await this.instance.save();

        this._formStateService.emitClearAKAEvent();

        this._tabControlService.announceTabFinishedLoading(this.currentTab.id);
        this._formStateService.emitClearAKAEvent();
        return result;
    }
}
